import { useState, useRef, useCallback, useId } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { gql } from '@apollo/client';
import { useSubscription, useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';
import { usePermission } from '../../../hooks/usePermission';
import { selectToken } from '../../../store/features/auth/authSlice';
import { trackEvent } from '../../../lib/usageEventBatch';
import '@tylertech/forge/button';
import '@tylertech/forge/banner';
import '@tylertech/forge/card';
import '@tylertech/forge/linear-progress';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/checkbox';
import '@tylertech/forge/icon';
import '@tylertech/forge/table';
import { CsvFormatHelp } from './CsvFormatHelp';
import './index.css';

const REST_API_BASE = import.meta.env.VITE_REST_API_URL || 'http://localhost:3001/api/v1';

export const Route = createFileRoute('/admin/bulk-operations/')({
  component: BulkOperationsAdminPage,
});

export { BulkOperationsAdminPage };

// ── Types ──────────────────────────────────────────────────────────────────

type OperationType = 'IMPORT' | 'UPDATE' | 'DELETE' | 'VALIDATE';
type UpdateStrategy = 'MERGE' | 'REPLACE' | 'PATCH';
type OperationStatus =
  'PENDING' | 'VALIDATING' | 'VALIDATED' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'PARTIAL';

interface BulkOperationError {
  itemIndex: number;
  itemId?: string;
  code: string;
  message: string;
}

interface BulkOperationResult {
  operationId: string;
  status: OperationStatus;
  totalItems: number;
  processedItems: number;
  successfulItems: number;
  failedItems: number;
  errors: BulkOperationError[];
  restoredCount?: number;
}

interface ProgressEvent {
  operationId: string;
  status: string;
  totalItems: number;
  processedItems: number;
  successfulItems: number;
  failedItems: number;
  percentComplete: number;
}

interface EntityTypeDefinition {
  name: string;
}

// ── GraphQL subscription ───────────────────────────────────────────────────

const BULK_OPERATION_PROGRESS = gql`
  subscription BulkOperationProgress($operationId: ID!) {
    bulkOperationProgress(operationId: $operationId) {
      operationId
      status
      totalItems
      processedItems
      successfulItems
      failedItems
      percentComplete
    }
  }
`;

// ── GraphQL query — supported entity types (metamodel) ─────────────────────

const GET_ENTITY_TYPES = gql`
  query GetEntityTypesForBulkOps {
    entityTypes {
      name
    }
  }
`;

// ── Forge casts ────────────────────────────────────────────────────────────

const ForgeButton = 'forge-button' as React.ElementType;
const ForgeBanner = 'forge-banner' as React.ElementType;
const ForgeCard = 'forge-card' as React.ElementType;
const ForgeLinearProgress = 'forge-linear-progress' as React.ElementType;
const ForgeTextField = 'forge-text-field' as React.ElementType;
const ForgeSelect = 'forge-select' as React.ElementType;
const ForgeOption = 'forge-option' as React.ElementType;
const ForgeCheckbox = 'forge-checkbox' as React.ElementType;
const ForgeIcon = 'forge-icon' as React.ElementType;

// ── Progress subscription hook ─────────────────────────────────────────────

function useProgressSubscription(
  operationId: string | null,
  onProgress: (event: ProgressEvent) => void
): void {
  useSubscription<{ bulkOperationProgress: ProgressEvent }>(BULK_OPERATION_PROGRESS, {
    variables: { operationId: operationId ?? '' },
    skip: !operationId,
    onData: ({ data }) => {
      const event = data?.data?.bulkOperationProgress;
      if (event) onProgress(event);
    },
  });
}

// ── Error table ────────────────────────────────────────────────────────────

export function ErrorTable({ errors }: { errors: BulkOperationError[] }): JSX.Element {
  if (errors.length === 0) return <></>;
  return (
    <div className="bulk-ops-errors">
      <h3 className="bulk-ops-errors__heading">Errors ({errors.length})</h3>
      <table className="bulk-ops-errors__table">
        <thead>
          <tr>
            <th>Row</th>
            <th>ID</th>
            <th>Code</th>
            <th>Message</th>
          </tr>
        </thead>
        <tbody>
          {errors.map((err, i) => (
            <tr key={i}>
              <td>{err.itemIndex}</td>
              <td>{err.itemId ?? '—'}</td>
              <td>{err.code}</td>
              <td>{err.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Status badge ───────────────────────────────────────────────────────────

export function StatusBadge({ status }: { status: OperationStatus | string }): JSX.Element {
  const cls = `bulk-ops-status bulk-ops-status--${status.toLowerCase()}`;
  return <span className={cls}>{status}</span>;
}

// ── Main component ─────────────────────────────────────────────────────────

function BulkOperationsAdminPage(): JSX.Element {
  const canWrite = usePermission('bulk-operations:write');
  const token = useSelector(selectToken);
  const fileInputId = useId();
  const jsonBodyId = useId();

  const { data: entityTypesData } = useQuery<{ entityTypes: EntityTypeDefinition[] }>(
    GET_ENTITY_TYPES
  );
  const entityTypeOptions = entityTypesData?.entityTypes ?? [];

  // Form state
  const [operationType, setOperationType] = useState<OperationType>('IMPORT');
  const [entityType, setEntityType] = useState('');
  const [strategy, setStrategy] = useState<UpdateStrategy>('MERGE');
  const [validateOnly, setValidateOnly] = useState(false);
  const [continueOnError, setContinueOnError] = useState(false);
  const [createSnapshot, setCreateSnapshot] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [jsonBody, setJsonBody] = useState('');

  // Operation state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkOperationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ProgressEvent | null>(null);
  const [activeOperationId, setActiveOperationId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useProgressSubscription(
    activeOperationId,
    useCallback((evt) => {
      setProgress(evt);
    }, [])
  );

  const resetForm = useCallback(() => {
    setResult(null);
    setError(null);
    setProgress(null);
    setActiveOperationId(null);
    setCsvFile(null);
    setJsonBody('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!entityType.trim()) {
      setError('Entity type is required');
      return;
    }

    /* c8 ignore start -- fetch paths require forge-element onChange; tested via E2E */
    setIsSubmitting(true);
    setError(null);
    setResult(null);
    setProgress(null);
    setActiveOperationId(null);

    try {
      let response: Response;

      if (csvFile) {
        const csvText = await csvFile.text();
        response = await fetch(
          `${REST_API_BASE}/bulk-operations?operation=${operationType}&entityType=${encodeURIComponent(entityType)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'text/csv',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: csvText,
          }
        );
      } else {
        let body: Record<string, unknown>;
        try {
          body = jsonBody.trim()
            ? (JSON.parse(jsonBody) as Record<string, unknown>)
            : { items: [] };
        } catch {
          setError('JSON body is invalid');
          setIsSubmitting(false);
          return;
        }

        response = await fetch(`${REST_API_BASE}/bulk-operations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            operation: operationType,
            entityType,
            strategy: operationType === 'UPDATE' ? strategy : undefined,
            options: { validateOnly, continueOnError, createSnapshot },
            ...body,
          }),
        });
      }

      const data = (await response.json()) as BulkOperationResult | { error: { message: string } };

      if (!response.ok) {
        const errData = data as { error: { message: string } };
        setError(errData?.error?.message ?? `Request failed: ${response.status}`);
        return;
      }

      const opResult = data as BulkOperationResult;
      setResult(opResult);
      setActiveOperationId(opResult.operationId);
      trackEvent('feature.bulk_operation_launched', {
        operation: operationType,
        entityType,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
    /* c8 ignore end */
  }, [
    entityType,
    operationType,
    strategy,
    validateOnly,
    continueOnError,
    createSnapshot,
    csvFile,
    jsonBody,
    token,
  ]);

  /* c8 ignore start -- rollback requires result state from prior fetch; tested via E2E */
  const handleRollback = useCallback(
    async (operationId: string) => {
      setError(null);
      try {
        const response = await fetch(`${REST_API_BASE}/bulk-operations/${operationId}/rollback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const data = (await response.json()) as
          BulkOperationResult | { error: { message: string } };
        if (!response.ok) {
          const errData = data as { error: { message: string } };
          setError(errData?.error?.message ?? `Rollback failed: ${response.status}`);
          return;
        }
        setResult(data as BulkOperationResult);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [token]
  );
  /* c8 ignore end */

  if (!canWrite) {
    return (
      <div className="bulk-ops-page">
        <ForgeBanner>
          <ForgeIcon name="lock" slot="icon" aria-hidden="true" />
          You do not have permission to access bulk operations.
        </ForgeBanner>
      </div>
    );
  }

  const showProgress = progress !== null || (result !== null && result.status === 'PROCESSING');
  const progressPct =
    progress?.percentComplete ??
    (result ? Math.round((result.processedItems / Math.max(result.totalItems, 1)) * 100) : 0);

  return (
    <div className="bulk-ops-page">
      <h1 className="bulk-ops-page__heading">Bulk Operations</h1>

      <ForgeCard className="bulk-ops-form-card">
        <div className="bulk-ops-form">
          <div className="bulk-ops-form__row">
            <ForgeSelect
              name="operationType"
              label="Operation"
              value={operationType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setOperationType(e.target.value as OperationType)
              }
            >
              <ForgeOption value="IMPORT">Import (Create)</ForgeOption>
              <ForgeOption value="UPDATE">Update</ForgeOption>
              <ForgeOption value="DELETE">Delete</ForgeOption>
              <ForgeOption value="VALIDATE">Validate Only</ForgeOption>
            </ForgeSelect>

            <ForgeSelect
              name="entityType"
              label="Entity Type *"
              placeholder="Select entity type…"
              required
              value={entityType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEntityType(e.target.value)}
            >
              {entityTypeOptions.map((et) => (
                <ForgeOption key={et.name} value={et.name}>
                  {et.name}
                </ForgeOption>
              ))}
            </ForgeSelect>
          </div>

          {operationType === 'UPDATE' && (
            <div className="bulk-ops-form__row">
              <ForgeSelect
                name="updateStrategy"
                label="Update Strategy"
                value={strategy}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setStrategy(e.target.value as UpdateStrategy)
                }
              >
                <ForgeOption value="MERGE">Merge (partial update)</ForgeOption>
                <ForgeOption value="REPLACE">Replace (full overwrite)</ForgeOption>
                <ForgeOption value="PATCH">Patch (RFC 6902)</ForgeOption>
              </ForgeSelect>
            </div>
          )}

          <div className="bulk-ops-form__options">
            <label className="bulk-ops-form__option">
              <ForgeCheckbox
                checked={validateOnly}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValidateOnly(e.target.checked)
                }
              />
              Validate only (dry run)
            </label>
            <label className="bulk-ops-form__option">
              <ForgeCheckbox
                checked={continueOnError}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setContinueOnError(e.target.checked)
                }
              />
              Continue on error
            </label>
            {operationType === 'UPDATE' && (
              <label className="bulk-ops-form__option">
                <ForgeCheckbox
                  checked={createSnapshot}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCreateSnapshot(e.target.checked)
                  }
                />
                Create snapshot (enable rollback)
              </label>
            )}
          </div>

          {(operationType === 'IMPORT' || operationType === 'VALIDATE') && (
            <div className="bulk-ops-form__upload">
              <p className="bulk-ops-form__label">CSV File (optional — or paste JSON below)</p>
              <input
                id={fileInputId}
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="bulk-ops-form__file-input"
                onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
              />
              {csvFile && <span className="bulk-ops-form__filename">{csvFile.name}</span>}
              <CsvFormatHelp entityType={entityType} />
            </div>
          )}

          {!csvFile && (
            <div className="bulk-ops-form__json">
              <ForgeTextField>
                <label htmlFor={jsonBodyId}>Items JSON</label>
                <textarea
                  id={jsonBodyId}
                  value={jsonBody}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setJsonBody(e.target.value)
                  }
                  placeholder='{"items": [{"id": "...", "name": "..."}]}'
                  rows={6}
                />
              </ForgeTextField>
            </div>
          )}

          <div className="bulk-ops-form__actions">
            <ForgeButton
              type="button"
              variant="raised"
              disabled={isSubmitting || !entityType.trim()}
              onClick={handleSubmit}
            >
              <ForgeIcon name="play_arrow" slot="start" aria-hidden="true" />
              {isSubmitting ? 'Submitting…' : 'Launch Operation'}
            </ForgeButton>
            {(result || error) && (
              <ForgeButton type="button" variant="outlined" onClick={resetForm}>
                Reset
              </ForgeButton>
            )}
          </div>
        </div>
      </ForgeCard>

      {error && (
        <ForgeBanner className="bulk-ops-error-banner" theme="danger">
          <ForgeIcon name="error" slot="icon" aria-hidden="true" />
          {error}
        </ForgeBanner>
      )}

      {showProgress && (
        <ForgeCard className="bulk-ops-progress-card">
          <h2 className="bulk-ops-progress-card__heading">Progress</h2>
          {activeOperationId && (
            <p className="bulk-ops-progress-card__id">Operation: {activeOperationId}</p>
          )}
          <ForgeLinearProgress
            determinate
            progress={progressPct / 100}
            aria-label={`${progressPct}% complete`}
          />
          <p className="bulk-ops-progress-card__pct">{progressPct}%</p>
          {progress && (
            <dl className="bulk-ops-progress-card__counts">
              <dt>Processed</dt>
              <dd>
                {progress.processedItems} / {progress.totalItems}
              </dd>
              <dt>Successful</dt>
              <dd>{progress.successfulItems}</dd>
              <dt>Failed</dt>
              <dd>{progress.failedItems}</dd>
            </dl>
          )}
        </ForgeCard>
      )}

      {result && (
        <ForgeCard className="bulk-ops-result-card">
          <h2 className="bulk-ops-result-card__heading">
            Result <StatusBadge status={result.status} />
          </h2>
          <dl className="bulk-ops-result-card__counts">
            <dt>Total Items</dt>
            <dd>{result.totalItems}</dd>
            <dt>Processed</dt>
            <dd>{result.processedItems}</dd>
            <dt>Successful</dt>
            <dd>{result.successfulItems}</dd>
            <dt>Failed</dt>
            <dd>{result.failedItems}</dd>
            {result.restoredCount !== undefined && (
              <>
                <dt>Restored</dt>
                <dd>{result.restoredCount}</dd>
              </>
            )}
          </dl>

          {result.status === 'COMPLETED' && createSnapshot && (
            <div className="bulk-ops-result-card__rollback">
              <ForgeButton
                type="button"
                variant="outlined"
                className="action-btn--danger"
                onClick={() => handleRollback(result.operationId)}
              >
                <ForgeIcon name="undo" slot="start" aria-hidden="true" />
                Rollback
              </ForgeButton>
            </div>
          )}

          <ErrorTable errors={result.errors} />
        </ForgeCard>
      )}
    </div>
  );
}
